// Pure mapper: turns the AI draft flow into the ordered ChatMessage[] the chat
// UI renders. No React, no side effects — unit-testable in isolation. This is the
// ONLY place that knows the draft flow's shape; when streaming / server-side
// multi-turn arrives, only this mapper and its caller (`useAiChat`) change — the
// message components stay as-is.

import type {
  AiPhase,
  AiCalculationJob,
  AiJobStatus,
} from "../../../hooks/useAiDraft";
import { isRetryable } from "./chatCopy";

export interface FileChip {
  id: string;
  name: string;
}

/** One labelled row of the calculation "header" (scope) the user set in the
 *  wizard — rendered as a label→value pair in the opening scope card. */
export interface ScopeField {
  label: string;
  value: string;
}

/** A submitted turn snapshot — the note + files captured when the user hit send,
 *  kept stable across polls/retries so the user bubble doesn't mutate. */
export interface SubmittedTurn {
  text: string;
  files: FileChip[];
}

/** A past turn that ended without success (noData / failed). Success is never
 *  archived — it closes the panel. Kept so the thread reads as a conversation:
 *  earlier attempts stay visible above the current one. */
export interface CompletedTurn {
  /** Monotonic id so React keys stay stable as turns accumulate. */
  id: number;
  submitted: SubmittedTurn;
  phase: Extract<AiPhase, { kind: "noData" } | { kind: "failed" }>;
}

export type ChatMessage =
  | { id: string; role: "assistant"; kind: "welcome" }
  | { id: string; role: "user"; kind: "scope"; fields: ScopeField[] }
  | { id: string; role: "user"; text: string; files: FileChip[] }
  | { id: string; role: "assistant"; kind: "progress"; status: AiJobStatus }
  | { id: string; role: "assistant"; kind: "ready"; job: AiCalculationJob }
  | { id: string; role: "assistant"; kind: "noData"; job: AiCalculationJob }
  | {
      id: string;
      role: "assistant";
      kind: "failed";
      errorCode: string | null;
      summary: string | null;
      /** Whether this bubble offers an inline Retry. Only the current turn is
       *  retryable — archived failures are static history (retrying would hit
       *  the current job, not the old one). */
      retryable: boolean;
    };

export interface BuildMessagesOptions {
  /** The calculation header the user set in the wizard, as labelled rows —
   *  rendered as the opening scope card so the thread starts with their context.
   *  Empty/omitted → no scope card. */
  scopeFields?: ScopeField[];
  /** Past non-success turns, oldest first. */
  history?: CompletedTurn[];
}

/** The assistant result bubble for a past (archived) turn — never retryable. */
function archivedResult(turn: CompletedTurn): ChatMessage {
  const id = `result-${turn.id}`;
  if (turn.phase.kind === "noData") {
    return { id, role: "assistant", kind: "noData", job: turn.phase.job };
  }
  return {
    id,
    role: "assistant",
    kind: "failed",
    errorCode: turn.phase.job?.errorCode ?? null,
    summary: turn.phase.job?.summary ?? null,
    retryable: false,
  };
}

/**
 * Build the whole thread: the pinned scope + welcome, then each archived turn
 * (user bubble + its result), then the current in-flight / just-finished turn
 * derived from `phase`. Message ids are stable per logical message so React keys
 * don't thrash between polls.
 */
export function buildMessages(
  phase: AiPhase,
  submitted: SubmittedTurn | null,
  options: BuildMessagesOptions = {},
): ChatMessage[] {
  const { scopeFields = [], history = [] } = options;
  const messages: ChatMessage[] = [];

  if (scopeFields.length > 0) {
    messages.push({
      id: "scope",
      role: "user",
      kind: "scope",
      fields: scopeFields,
    });
  }
  messages.push({ id: "welcome", role: "assistant", kind: "welcome" });

  for (const turn of history) {
    messages.push({
      id: `user-${turn.id}`,
      role: "user",
      text: turn.submitted.text,
      files: turn.submitted.files,
    });
    messages.push(archivedResult(turn));
  }

  // Current turn: nothing beyond scope/welcome until the first submit.
  if (phase.kind === "input") return messages;

  if (submitted) {
    messages.push({
      id: "user-current",
      role: "user",
      text: submitted.text,
      files: submitted.files,
    });
  }

  if (phase.kind === "running") {
    messages.push({
      id: "assistant-progress",
      role: "assistant",
      kind: "progress",
      status: phase.status,
    });
  } else if (phase.kind === "ready") {
    messages.push({
      id: "assistant-result",
      role: "assistant",
      kind: "ready",
      job: phase.job,
    });
  } else if (phase.kind === "noData") {
    messages.push({
      id: "assistant-result",
      role: "assistant",
      kind: "noData",
      job: phase.job,
    });
  } else if (phase.kind === "failed") {
    const errorCode = phase.job?.errorCode ?? null;
    messages.push({
      id: "assistant-result",
      role: "assistant",
      kind: "failed",
      errorCode,
      summary: phase.job?.summary ?? null,
      retryable: isRetryable(errorCode),
    });
  }

  return messages;
}
